import { User } from '../../types';
import { hasUnlimitedTokens } from '../../utils/promoEffectsManager';
import { TokenConfig, getTokenConfig } from './config';
import { getAllTransactions, getTransactionHistory } from './transactions';

/**
 * Get analytics for admin dashboard
 */
export const getAnalytics = (timeRange: 'today' | 'week' | 'month' | 'all' = 'today'): {
    totalTransactions: number;
    totalTokensCharged: number;
    totalTokensRefunded: number;
    averagePerTransaction: number;
    byOperationType: Record<string, number>;
    byComplexity: Record<string, number>;
    byModel: Record<string, number>;
} => {
    const transactions = getAllTransactions();
    const now = Date.now();
    const ranges = {
        today: 24 * 60 * 60 * 1000,
        week: 7 * 24 * 60 * 60 * 1000,
        month: 30 * 24 * 60 * 60 * 1000,
        all: Infinity,
    };

    const filtered = transactions.filter(
        tx => now - tx.timestamp < ranges[timeRange]
    );

    const totalTokensCharged = filtered
        .filter(tx => tx.charged > 0)
        .reduce((sum, tx) => sum + tx.charged, 0);

    const totalTokensRefunded = Math.abs(
        filtered
            .filter(tx => tx.charged < 0)
            .reduce((sum, tx) => sum + tx.charged, 0)
    );

    const byOperationType: Record<string, number> = {};
    const byComplexity: Record<string, number> = {};
    const byModel: Record<string, number> = {};

    filtered.forEach(tx => {
        byOperationType[tx.operationType] = (byOperationType[tx.operationType] || 0) + tx.charged;
        byComplexity[tx.metadata.complexity] = (byComplexity[tx.metadata.complexity] || 0) + tx.charged;
        byModel[tx.metadata.model] = (byModel[tx.metadata.model] || 0) + tx.charged;
    });

    return {
        totalTransactions: filtered.length,
        totalTokensCharged,
        totalTokensRefunded,
        averagePerTransaction: filtered.length > 0 ? totalTokensCharged / filtered.length : 0,
        byOperationType,
        byComplexity,
        byModel,
    };
};

/**
 * Get token usage report for a user
 */
export const getTokenUsageReport = (user: User, config?: TokenConfig) => {
    const tokenConfig = config || getTokenConfig();
    const isPremium = user.isPremium;
    const isMax = (user as any).isMax;

    let dailyLimit: number;
    if (isMax) {
        dailyLimit = tokenConfig.limits.maxUserDaily;
    } else if (isPremium) {
        dailyLimit = tokenConfig.limits.premiumUserDaily;
    } else {
        dailyLimit = tokenConfig.limits.standardUserDaily;
    }

    const currentUsage = user.dailyTokenUsage || 0;
    const remaining = Math.max(0, dailyLimit - currentUsage);
    const usagePercent = dailyLimit > 0 ? (currentUsage / dailyLimit) * 100 : 0;

    return {
        dailyLimit,
        currentUsage,
        remaining,
        usagePercent: Math.min(100, usagePercent),
        hasUnlimited: hasUnlimitedTokens(user),
        transactions: getTransactionHistory(user.id, 10)
    };
};
